/**
 * SEO Component - React Helmet alternative
 * Updates meta tags and structured data for each page.
 *
 * Supports MULTIPLE coexisting JSON-LD blocks (id-keyed) and re-runs its
 * effect only when the CONTENT changes — callers pass fresh object literals
 * on every render, so depending on object identity would rewrite the whole
 * head on each parent render (e.g. every chat keystroke on the homepage).
 */
import { useEffect } from 'react';
import {
  updateMetaTags,
  insertStructuredData,
  generateStructuredData,
  SEOConfig,
  StructuredDataType,
} from '../utils/seo';

export interface StructuredDataEntry {
  type: StructuredDataType;
  data?: any;
  /** Distinct key per block; defaults to the lowercased type. */
  id?: string;
}

interface SEOProps extends SEOConfig {
  structuredData?: StructuredDataEntry | StructuredDataEntry[];
}

export function SEO({ structuredData, ...config }: SEOProps) {
  // Serialize to primitives so the effect keys on content, not identity.
  const configKey = JSON.stringify(config);
  const structuredKey = JSON.stringify(structuredData ?? null);

  useEffect(() => {
    updateMetaTags(JSON.parse(configKey) as SEOConfig);

    const parsed = JSON.parse(structuredKey) as
      | StructuredDataEntry
      | StructuredDataEntry[]
      | null;
    if (parsed) {
      const entries = Array.isArray(parsed) ? parsed : [parsed];
      for (const entry of entries) {
        const jsonLd = generateStructuredData(entry.type, entry.data || {});
        insertStructuredData(jsonLd, entry.id ?? entry.type.toLowerCase());
      }
    }
  }, [configKey, structuredKey]);

  return null; // This component doesn't render anything
}

export default SEO;
